import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useId,
  useInsertionEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  BiAlignLeft,
  BiAlignRight,
  BiBold,
  BiCode,
  BiItalic,
  BiLink,
  BiListOl,
  BiListUl,
  BiRedo,
  BiStrikethrough,
  BiText,
  BiUnderline,
  BiUndo,
  BiUnlink,
} from "react-icons/bi";
import {
  MdFormatAlignCenter,
  MdFormatAlignJustify,
  MdFormatIndentDecrease,
  MdFormatIndentIncrease,
} from "react-icons/md";
import { CustomButton } from "./CustomButton";
import { getHtmlContentStyles } from "../../utils/htmlContentStyles";
import { palette } from "../../utils/palette";
import { RichTextProfile, sanitizeHtml } from "../../utils/sanitizeHtml";
import {
  HTML_SIZE_FACTOR,
  countCharacters,
  textOf,
} from "../../utils/richText";

export interface HtmlEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  placeholder?: string;
  /** Accessible name of the text area; the placeholder when absent. */
  ariaLabel?: string;
  minHeight?: string;
  maxHeight?: string;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  maxLength?: number;
  showCharacterCount?: boolean;
  onCharacterLimitExceeded?: (exceeded: boolean) => void;
  onValidationChange?: (isValid: boolean, message?: string) => void;
  /** "basic" offers emphasis, lists and emoji only, and keeps nothing else from a paste. */
  formatting?: RichTextProfile;
}

export interface HtmlEditorRef {
  focus: () => void;
  blur: () => void;
  getHtml: () => string;
  /** setHtml and clear call onChange before returning: build the update from the latest state, not a captured copy. */
  setHtml: (html: string) => void;
  isEmpty: () => boolean;
  clear: () => void;
}

const BLOCK_TAGS = ["p", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote"];

const FONT_SIZES: Record<string, string> = {
  Small: "1",
  Normal: "3",
  Large: "4",
  Huge: "6",
};

const TOGGLE_COMMANDS = [
  "bold",
  "italic",
  "underline",
  "strikeThrough",
  "subscript",
  "superscript",
  "insertUnorderedList",
  "insertOrderedList",
  "justifyLeft",
  "justifyCenter",
  "justifyRight",
  "justifyFull",
] as const;

type ToggleCommand = (typeof TOGGLE_COMMANDS)[number];

const TOGGLE_LABELS: Record<ToggleCommand, string> = {
  bold: "bold",
  italic: "italic",
  underline: "underline",
  strikeThrough: "strikeThrough",
  subscript: "subscript",
  superscript: "superscript",
  insertUnorderedList: "bulletList",
  insertOrderedList: "numberedList",
  justifyLeft: "alignLeft",
  justifyCenter: "alignCenter",
  justifyRight: "alignRight",
  justifyFull: "justify",
};

interface ToolbarState {
  block: string;
  fontSize: string;
  active: Partial<Record<ToggleCommand, boolean>>;
}

const INITIAL_TOOLBAR: ToolbarState = {
  block: "p",
  fontSize: "Normal",
  active: {},
};

const EMOJIS = [
  "😊",
  "😂",
  "🙂",
  "😍",
  "🤔",
  "😉",
  "😎",
  "😅",
  "👍",
  "👎",
  "👏",
  "🙌",
  "👌",
  "🙏",
  "👋",
  "✌",
  "❤",
  "💙",
  "💚",
  "💛",
  "🧡",
  "💜",
  "🖤",
  "💕",
  "🎉",
  "⭐",
  "🔥",
  "💯",
  "✨",
  "🎁",
  "🏆",
  "⚡",
];

const isMac =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

const shortcut = (key: string, shift = false) =>
  isMac ? `${shift ? "⇧" : ""}⌘${key}` : `Ctrl+${shift ? "Shift+" : ""}${key}`;

const DECORATION_TAGS = ["U", "S", "STRIKE"];

// "Moyen" is the text's own size: the size="3" the browser writes for it is dropped, its colour kept.
const dropNormalSize = (doc: Document) =>
  Array.from(doc.body.querySelectorAll('font[size="3"]')).forEach((font) => {
    if (font.attributes.length > 1) font.removeAttribute("size");
    else font.replaceWith(...Array.from(font.childNodes));
  });

// A colour inside an underline or a strike leaves the line in the outer colour:
// it moves outside when it holds the whole run, which never changes the text.
const liftColours = (doc: Document) =>
  Array.from(doc.body.querySelectorAll("font[color]")).forEach((font) => {
    const decoration = font.parentElement;
    if (
      !decoration ||
      !DECORATION_TAGS.includes(decoration.tagName) ||
      decoration.childNodes.length !== 1
    )
      return;
    decoration.replaceWith(font);
    decoration.append(...Array.from(font.childNodes));
    font.append(decoration);
  });

const tidyFonts = (html: string): string => {
  if (!html.includes("<font")) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  dropNormalSize(doc);
  liftColours(doc);
  return doc.body.innerHTML;
};

// An emptied editor keeps a <br> or an empty list item: it is sent as "", the value the forms keep for it.
export const toEditorHtml = (
  dirty: string,
  profile: RichTextProfile = "full"
): string => {
  const html = tidyFonts(sanitizeHtml(dirty, profile));
  return countCharacters(textOf(html).trim()) === 0 ? "" : html;
};

const escapeHtml = (text: string) =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const toHexColour = (value: string, fallback: string): string => {
  if (/^#[0-9a-f]{6}$/i.test(value)) return value;
  const match = value.match(
    /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/
  );
  if (!match || match[4] === "0") return fallback;
  return `#${match
    .slice(1, 4)
    .map((channel) => Number(channel).toString(16).padStart(2, "0"))
    .join("")}`;
};

const queryState = (command: ToggleCommand) => {
  try {
    return document.queryCommandState(command);
  } catch {
    return false;
  }
};

const queryValue = (command: string) => {
  try {
    return document.queryCommandValue(command);
  } catch {
    return "";
  }
};

const blockElementOf = (node: Node, editor: HTMLElement): Element | null => {
  let current: Element | null =
    node.nodeType === Node.ELEMENT_NODE
      ? (node as Element)
      : node.parentElement;
  while (current && current !== editor) {
    const tag = current.tagName.toLowerCase();
    if (tag === "div" || BLOCK_TAGS.includes(tag)) return current;
    current = current.parentElement;
  }
  return null;
};

const blockTagOf = (node: Node, editor: HTMLElement) => {
  const tag = blockElementOf(node, editor)?.tagName.toLowerCase();
  return tag && BLOCK_TAGS.includes(tag) ? tag : "p";
};

const caretRangeAt = (x: number, y: number): Range | null => {
  if (document.caretRangeFromPoint) return document.caretRangeFromPoint(x, y);
  const position = document.caretPositionFromPoint?.(x, y);
  if (!position) return null;
  const range = document.createRange();
  range.setStart(position.offsetNode, position.offset);
  range.collapse(true);
  return range;
};

const toLinkUrl = (input: string): string | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  try {
    const url = new URL(candidate);
    return (url.protocol === "https:" || url.protocol === "http:") &&
      url.hostname.includes(".")
      ? url.href
      : null;
  } catch {
    return null;
  }
};

const firstTextIn = (range: Range): Node => {
  const root = range.commonAncestorContainer;
  if (root.nodeType === Node.TEXT_NODE) return root;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    if (range.intersectsNode(node) && node !== range.endContainer) return node;
    node = walker.nextNode();
  }
  return range.startContainer;
};

// Read from the computed style: moving the selection to query it would take the focus from the colour field.
const colourAt = (
  range: Range | null,
  editor: HTMLElement | null,
  property: "color" | "background-color",
  fallback: string
): string => {
  if (!range || !editor) return fallback;
  const node = firstTextIn(range);
  let element: Element | null =
    node.nodeType === Node.ELEMENT_NODE
      ? (node as Element)
      : node.parentElement;
  while (element && editor.contains(element)) {
    const value = getComputedStyle(element).getPropertyValue(property);
    const colour = toHexColour(value, "");
    if (colour) return colour;
    if (element === editor) break;
    element = element.parentElement;
  }
  return fallback;
};

interface Caret {
  start: number[];
  startOffset: number;
  end: number[];
  endOffset: number;
}

interface Snapshot {
  html: string;
  caret: Caret | null;
}

type EditKind = "typing" | "deleting" | "other";

const HISTORY_LIMIT = 100;
const TYPING_GROUP_MS = 1000;

const pathTo = (root: Node, node: Node): number[] | null => {
  const path: number[] = [];
  let current: Node = node;
  while (current !== root) {
    const parent = current.parentNode;
    if (!parent) return null;
    path.unshift(Array.prototype.indexOf.call(parent.childNodes, current));
    current = parent;
  }
  return path;
};

const nodeAt = (root: Node, path: number[]): Node | null =>
  path.reduce<Node | null>(
    (node, index) => node?.childNodes[index] ?? null,
    root
  );

const lastOffset = (node: Node) =>
  node.nodeType === Node.TEXT_NODE
    ? (node as Text).length
    : node.childNodes.length;

const caretIn = (root: HTMLElement): Caret | null => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return null;
  const start = pathTo(root, range.startContainer);
  const end = pathTo(root, range.endContainer);
  return start && end
    ? { start, startOffset: range.startOffset, end, endOffset: range.endOffset }
    : null;
};

const placeCaret = (root: HTMLElement, caret: Caret | null) => {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  const start = caret && nodeAt(root, caret.start);
  const end = caret && nodeAt(root, caret.end);
  if (caret && start && end) {
    range.setStart(start, Math.min(caret.startOffset, lastOffset(start)));
    range.setEnd(end, Math.min(caret.endOffset, lastOffset(end)));
  } else {
    range.selectNodeContents(root);
    range.collapse(false);
  }
  selection.removeAllRanges();
  selection.addRange(range);
};

const editKindOf = (inputType?: string): EditKind =>
  inputType === "insertText" || inputType === "insertCompositionText"
    ? "typing"
    : inputType === "deleteContentBackward" ||
      inputType === "deleteContentForward" ||
      inputType === "deleteWordBackward" ||
      inputType === "deleteWordForward"
    ? "deleting"
    : "other";

const allToolbarItems = (toolbar: HTMLElement | null): HTMLElement[] =>
  Array.from(
    toolbar?.querySelectorAll<HTMLElement>("[data-toolbar-item]") ?? []
  );

const toolbarItems = (toolbar: HTMLElement | null): HTMLElement[] =>
  allToolbarItems(toolbar).filter((item) => !item.hasAttribute("disabled"));

const focusOnly = (
  toolbar: HTMLElement | null,
  target: HTMLElement | undefined
) =>
  allToolbarItems(toolbar).forEach((item) => {
    item.tabIndex = item === target ? 0 : -1;
  });

// Erasing everything leaves a <br> in the browser, which :empty does not match.
const BLANK_HTML = /^(<(p|div)>)?(<br\s*\/?>)?(<\/(p|div)>)?$/i;

const STYLE_ELEMENT_ID = "html-editor-styles";

const EDITOR_STYLES = `
  .html-editor [contenteditable][data-blank]:before {
    content: attr(data-placeholder);
    color: ${palette.gray400};
    pointer-events: none;
    display: block;
    height: 0;
  }
  .html-editor code {
    background-color: ${palette.gray100};
    padding: 2px 4px;
    border-radius: 3px;
    font-family: monospace;
    font-size: 0.9em;
  }
  .html-editor h1 {
    font-size: 2em;
    font-weight: bold;
    margin: 0.67em 0;
    line-height: 1.2;
  }
  .html-editor h2 {
    font-size: 1.5em;
    font-weight: bold;
    margin: 0.75em 0;
    line-height: 1.2;
  }
  .html-editor h3 {
    font-size: 1.17em;
    font-weight: bold;
    margin: 0.83em 0;
    line-height: 1.2;
  }
  .html-editor h4 {
    font-size: 1em;
    font-weight: bold;
    margin: 1.12em 0;
    line-height: 1.2;
  }
  .html-editor h5 {
    font-size: 0.83em;
    font-weight: bold;
    margin: 1.5em 0;
    line-height: 1.2;
  }
  .html-editor h6 {
    font-size: 0.75em;
    font-weight: bold;
    margin: 1.67em 0;
    line-height: 1.2;
  }
  .html-editor blockquote {
    margin: 1em 0;
    padding-left: 1em;
    border-left: 4px solid ${palette.gray200};
    font-style: italic;
    color: ${palette.gray500};
  }
  .html-editor p {
    margin: 0.5em 0;
    line-height: 1.5;
  }
  .html-editor ul, .html-editor ol {
    margin: 0.5em 0;
    padding-left: 2em;
  }
  .html-editor li {
    margin: 0.25em 0;
    line-height: 1.4;
  }
  .html-editor strong, .html-editor b {
    font-weight: bold;
  }
  .html-editor em, .html-editor i {
    font-style: italic;
  }
  .html-editor u {
    text-decoration: underline;
  }
  .html-editor s, .html-editor strike {
    text-decoration: line-through;
  }
  .html-editor sub {
    vertical-align: sub;
    font-size: smaller;
  }
  .html-editor sup {
    vertical-align: super;
    font-size: smaller;
  }
  .html-editor a {
    color: ${palette.blue500};
    text-decoration: underline;
  }
  .html-editor a:hover {
    color: ${palette.blue700};
  }
  ${getHtmlContentStyles("html-editor")}
`;

const injectEditorStyles = () => {
  if (document.getElementById(STYLE_ELEMENT_ID)) return;
  const element = document.createElement("style");
  element.id = STYLE_ELEMENT_ID;
  element.textContent = EDITOR_STYLES;
  document.head.appendChild(element);
};

const toolButtonClass = (pressed?: boolean) =>
  `p-2 rounded-md hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
    pressed ? "bg-blue-100 text-blue-600" : "text-gray-600"
  }`;

interface ToolbarButtonProps {
  icon: React.ReactNode;
  label: string;
  onAction: () => void;
  pressed?: boolean;
  disabled?: boolean;
  keys?: string;
  expanded?: boolean;
  controls?: string;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  icon,
  label,
  onAction,
  pressed,
  disabled,
  keys,
  expanded,
  controls,
}) => (
  <button
    type="button"
    data-toolbar-item=""
    aria-label={label}
    aria-pressed={pressed}
    aria-expanded={expanded}
    aria-controls={controls}
    title={keys ? `${label} (${keys})` : label}
    disabled={disabled}
    onMouseDown={(event) => event.preventDefault()}
    onClick={onAction}
    className={toolButtonClass(pressed ?? expanded)}
  >
    {icon}
  </button>
);

interface ToolbarSelectProps {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onPick: (value: string) => void;
  disabled?: boolean;
}

const ToolbarSelect: React.FC<ToolbarSelectProps> = ({
  label,
  value,
  options,
  onPick,
  disabled,
}) => (
  <select
    data-toolbar-item=""
    aria-label={label}
    title={label}
    value={value}
    disabled={disabled}
    onChange={(event) => onPick(event.target.value)}
    className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
  >
    {options.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
);

interface ColourPickerProps {
  label: string;
  swatch: React.ReactNode;
  disabled?: boolean;
  onOpen: (input: HTMLInputElement) => void;
  onPick: (colour: string) => void;
}

const LIVE_COLOUR_DELAY_MS = 120;

const ColourPicker: React.FC<ColourPickerProps> = ({
  label,
  swatch,
  disabled,
  onOpen,
  onPick,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const pick = useRef(onPick);
  const prepare = useRef(onOpen);

  useEffect(() => {
    pick.current = onPick;
    prepare.current = onOpen;
  });

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    // The macOS colour panel sends "input" for each pick and "change" only when it closes:
    // the colour follows the picks, a few at a time, so that undo keeps a usable history.
    let timer: number | undefined;
    let applied = "";
    const apply = () => {
      window.clearTimeout(timer);
      if (input.value === applied) return;
      applied = input.value;
      pick.current(input.value);
    };
    const live = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(apply, LIVE_COLOUR_DELAY_MS);
    };
    const open = () => {
      prepare.current(input);
      applied = input.value;
    };
    input.addEventListener("input", live);
    input.addEventListener("change", apply);
    input.addEventListener("focus", open);
    input.addEventListener("pointerdown", open);
    return () => {
      window.clearTimeout(timer);
      input.removeEventListener("input", live);
      input.removeEventListener("change", apply);
      input.removeEventListener("focus", open);
      input.removeEventListener("pointerdown", open);
    };
  }, []);

  return (
    <span className="relative inline-flex" title={label}>
      <input
        ref={inputRef}
        type="color"
        data-toolbar-item=""
        aria-label={label}
        disabled={disabled}
        className="peer absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
      />
      <span
        aria-hidden="true"
        className={`${toolButtonClass()} pointer-events-none peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500`}
      >
        {swatch}
      </span>
    </span>
  );
};

const HtmlEditor = forwardRef<HtmlEditorRef, HtmlEditorProps>(
  (
    {
      value = "",
      onChange,
      onBlur,
      onFocus,
      placeholder,
      ariaLabel,
      minHeight = "200px",
      maxHeight = "400px",
      disabled = false,
      className = "",
      style = {},
      maxLength,
      showCharacterCount = false,
      onCharacterLimitExceeded,
      onValidationChange,
      formatting = "full",
    },
    ref
  ) => {
    const { t } = useTranslation();
    const instanceId = useId();
    const linkPanelId = `${instanceId}-link`;
    const emojiPanelId = `${instanceId}-emoji`;
    const editorRef = useRef<HTMLDivElement>(null);
    const toolbarRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const linkInputRef = useRef<HTMLInputElement>(null);
    const savedRange = useRef<Range | null>(null);
    // What the editor shows, and the last value prop it applied: only a new prop that is not
    // the echo of what the editor just sent may replace the content under the caret.
    const syncedHtml = useRef<string | null>(null);
    const appliedValue = useRef<string | null>(null);
    const dragFromEditor = useRef(false);
    const toolbarFocus = useRef<HTMLElement | null>(null);
    const [characterCount, setCharacterCount] = useState(0);
    const [toolbar, setToolbar] = useState<ToolbarState>(INITIAL_TOOLBAR);
    const [panel, setPanel] = useState<"emoji" | "link" | null>(null);
    const [linkUrl, setLinkUrl] = useState("");
    const [linkError, setLinkError] = useState("");
    // The browser keeps one undo history per page: with two editors, its undo would reach into the other one.
    const history = useRef({
      entries: [] as Snapshot[],
      index: -1,
      kind: "other" as EditKind,
      at: 0,
    });
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);

    const full = formatting === "full";
    const toHtml = useCallback(
      (dirty: string) => toEditorHtml(dirty, formatting),
      [formatting]
    );

    const placeholderText =
      placeholder ?? (t("htmlEditor.placeholder") as string);

    useInsertionEffect(injectEditorStyles, []);

    const reportLength = useCallback(
      (html: string) => {
        const count = countCharacters(textOf(html));
        setCharacterCount(count);
        if (!maxLength) return;
        const tooLong = count > maxLength;
        const tooHeavy = html.length > maxLength * HTML_SIZE_FACTOR;
        onCharacterLimitExceeded?.(tooLong || tooHeavy);
        onValidationChange?.(
          !tooLong && !tooHeavy,
          tooLong
            ? (t("htmlEditor.tooLong", { count, max: maxLength }) as string)
            : tooHeavy
            ? (t("htmlEditor.tooHeavy") as string)
            : undefined
        );
      },
      [maxLength, onCharacterLimitExceeded, onValidationChange, t]
    );

    const emitChange = useCallback(() => {
      const editor = editorRef.current;
      if (!editor) return;
      const html = toHtml(editor.innerHTML);
      if (html === syncedHtml.current) return;
      syncedHtml.current = html;
      reportLength(html);
      onChange?.(html);
    }, [onChange, reportLength, toHtml]);

    const syncHistoryButtons = useCallback(() => {
      const { entries, index } = history.current;
      setCanUndo(index > 0);
      setCanRedo(index < entries.length - 1);
    }, []);

    const resetHistory = useCallback(() => {
      const editor = editorRef.current;
      if (!editor) return;
      history.current = {
        entries: [{ html: editor.innerHTML, caret: null }],
        index: 0,
        kind: "other",
        at: 0,
      };
      syncHistoryButtons();
    }, [syncHistoryButtons]);

    const recordEdit = useCallback(
      (kind: EditKind) => {
        const editor = editorRef.current;
        if (!editor) return;
        const state = history.current;
        const snapshot = { html: editor.innerHTML, caret: caretIn(editor) };
        if (state.entries[state.index]?.html === snapshot.html) {
          state.entries[state.index] = snapshot;
          return;
        }
        const now = Date.now();
        const grouped =
          kind !== "other" &&
          kind === state.kind &&
          now - state.at < TYPING_GROUP_MS &&
          state.index > 0 &&
          state.index === state.entries.length - 1;
        if (grouped) {
          state.entries[state.index] = snapshot;
        } else {
          state.entries = [
            ...state.entries.slice(0, state.index + 1),
            snapshot,
          ].slice(-HISTORY_LIMIT);
          state.index = state.entries.length - 1;
        }
        state.kind = kind;
        state.at = now;
        syncHistoryButtons();
      },
      [syncHistoryButtons]
    );

    const replaceContent = useCallback(
      (dirty: string) => {
        const editor = editorRef.current;
        if (!editor) return;
        const html = toHtml(dirty);
        editor.innerHTML = html;
        savedRange.current = null;
        syncedHtml.current = html;
        reportLength(html);
        onChange?.(html);
        recordEdit("other");
      },
      [onChange, reportLength, recordEdit, toHtml]
    );

    useEffect(() => {
      const editor = editorRef.current;
      if (!editor || value === appliedValue.current) return;
      appliedValue.current = value;
      if (value === syncedHtml.current) return;
      const html = toHtml(value || "");
      if (html === syncedHtml.current) return;
      syncedHtml.current = html;
      editor.innerHTML = html;
      savedRange.current = null;
      reportLength(html);
      resetHistory();
    }, [value, reportLength, resetHistory, toHtml]);

    const refreshToolbar = useCallback(() => {
      const editor = editorRef.current;
      const selection = window.getSelection();
      if (!editor || !selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      if (!editor.contains(range.commonAncestorContainer)) return;
      savedRange.current = range.cloneRange();
      const size = queryValue("fontSize");
      const next: ToolbarState = {
        block: blockTagOf(range.startContainer, editor),
        fontSize:
          Object.keys(FONT_SIZES).find((key) => FONT_SIZES[key] === size) ??
          "Normal",
        active: Object.fromEntries(
          TOGGLE_COMMANDS.map((command) => [command, queryState(command)])
        ),
      };
      setToolbar((previous) =>
        previous.block === next.block &&
        previous.fontSize === next.fontSize &&
        TOGGLE_COMMANDS.every(
          (command) => previous.active[command] === next.active[command]
        )
          ? previous
          : next
      );
    }, []);

    useEffect(() => {
      document.addEventListener("selectionchange", refreshToolbar);
      return () =>
        document.removeEventListener("selectionchange", refreshToolbar);
    }, [refreshToolbar]);

    // The live selection wins while it is in the text; the saved one stands in once the focus has left
    // for the toolbar, a panel or the colour field. A target overrides both (colour panel, drop point).
    const restoreSelection = useCallback((target?: Range | null) => {
      const editor = editorRef.current;
      if (!editor) return;
      const selection = window.getSelection();
      const live =
        selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
      const liveInText = Boolean(
        live && editor.contains(live.commonAncestorContainer)
      );
      if (document.activeElement !== editor)
        editor.focus({ preventScroll: true });
      const range = target ?? (liveInText ? null : savedRange.current);
      if (
        range &&
        selection &&
        editor.contains(range.commonAncestorContainer)
      ) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }, []);

    const executeCommand = useCallback(
      (command: string, commandValue?: string, target?: Range | null) => {
        if (disabled || !editorRef.current) return;
        restoreSelection(target);
        try {
          if (document.execCommand(command, false, commandValue)) emitChange();
        } catch (error) {
          console.error(`Failed to execute command: ${command}`, error);
        }
        refreshToolbar();
      },
      [disabled, restoreSelection, emitChange, refreshToolbar]
    );

    const stepHistory = useCallback(
      (step: -1 | 1) => {
        const editor = editorRef.current;
        const state = history.current;
        const target = state.index + step;
        if (disabled || !editor || target < 0 || target >= state.entries.length)
          return;
        state.index = target;
        state.kind = "other";
        const { html, caret } = state.entries[target];
        editor.innerHTML = html;
        if (document.activeElement !== editor)
          editor.focus({ preventScroll: true });
        placeCaret(editor, caret);
        savedRange.current = null;
        emitChange();
        refreshToolbar();
        syncHistoryButtons();
      },
      [disabled, emitChange, refreshToolbar, syncHistoryButtons]
    );

    useEffect(() => {
      const editor = editorRef.current;
      if (!editor) return;
      const fromMenu = (event: InputEvent) => {
        if (
          event.inputType !== "historyUndo" &&
          event.inputType !== "historyRedo"
        )
          return;
        event.preventDefault();
        stepHistory(event.inputType === "historyUndo" ? -1 : 1);
      };
      editor.addEventListener("beforeinput", fromMenu);
      return () => editor.removeEventListener("beforeinput", fromMenu);
    }, [stepHistory]);

    const handleInput = (event: React.FormEvent<HTMLDivElement>) => {
      emitChange();
      recordEdit(editKindOf((event.nativeEvent as InputEvent).inputType));
    };

    useEffect(() => {
      const editor = editorRef.current;
      if (!editor) return;
      const mark = () =>
        editor.toggleAttribute("data-blank", BLANK_HTML.test(editor.innerHTML));
      mark();
      const observer = new MutationObserver(mark);
      observer.observe(editor, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      return () => observer.disconnect();
    }, []);

    const insertTransfer = (
      data: DataTransfer | null,
      target?: Range | null
    ) => {
      if (!data) return;
      const html = data.getData("text/html");
      if (html) {
        executeCommand("insertHTML", toHtml(html), target);
        return;
      }
      const text = data.getData("text/plain");
      if (text) {
        executeCommand(
          "insertHTML",
          escapeHtml(text).replace(/\n/g, "<br>"),
          target
        );
      }
    };

    const isInListItem = () => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return false;
      const node = selection.getRangeAt(0).startContainer;
      const element =
        node.nodeType === Node.ELEMENT_NODE
          ? (node as Element)
          : node.parentElement;
      const item = element?.closest("li");
      return Boolean(item && editorRef.current?.contains(item));
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled || event.nativeEvent.isComposing || event.keyCode === 229)
        return;
      if (event.ctrlKey || event.metaKey) {
        const key = event.key.toLowerCase();
        if (key === "z" || (key === "y" && !isMac)) {
          event.preventDefault();
          stepHistory(key === "y" || event.shiftKey ? 1 : -1);
          return;
        }
        const command =
          key === "b"
            ? "bold"
            : key === "i"
            ? "italic"
            : key === "u"
            ? "underline"
            : null;
        if (command) {
          event.preventDefault();
          executeCommand(command);
        }
        return;
      }
      // A line break rather than a new block outside lists: the text stays one paragraph in e-mails.
      if (event.key === "Enter" && !event.shiftKey && !isInListItem()) {
        event.preventDefault();
        executeCommand("insertLineBreak");
      }
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      event.preventDefault();
      insertTransfer(event.clipboardData);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (dragFromEditor.current) {
        dragFromEditor.current = false;
        return;
      }
      event.preventDefault();
      insertTransfer(
        event.dataTransfer,
        caretRangeAt(event.clientX, event.clientY)
      );
    };

    const applyBlock = (tag: string) => {
      const editor = editorRef.current;
      if (disabled || !editor || !BLOCK_TAGS.includes(tag)) return;
      restoreSelection();
      const selection = window.getSelection();
      const range =
        selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
      const block = range && blockElementOf(range.startContainer, editor);
      if (
        range &&
        !range.collapsed &&
        block &&
        block === blockElementOf(range.endContainer, editor) &&
        range.toString().trim() !== block.textContent?.trim()
      ) {
        const selected = document.createElement("div");
        selected.appendChild(range.cloneContents());
        executeCommand(
          "insertHTML",
          `<${tag}>${toHtml(selected.innerHTML)}</${tag}>`
        );
        return;
      }
      executeCommand("formatBlock", `<${tag}>`);
    };

    // Outside a list, Chrome indents with margins that are not kept on save: a quote is what gets saved and shown.
    const indent = () => {
      restoreSelection();
      if (isInListItem()) executeCommand("indent");
      else executeCommand("formatBlock", "<blockquote>");
    };

    const applyFontSize = (size: string) => {
      if (FONT_SIZES[size]) executeCommand("fontSize", FONT_SIZES[size]);
    };

    const insertInlineCode = () => {
      const selected = window.getSelection()?.toString() ?? "";
      const code = document.createElement("code");
      code.textContent = selected || "\u200B";
      executeCommand("insertHTML", code.outerHTML);
    };

    const insertEmoji = (emoji: string) => {
      executeCommand("insertText", emoji);
      setPanel(null);
    };

    const closePanel = () => {
      setPanel(null);
      restoreSelection();
    };

    const openLinkPanel = () => {
      refreshToolbar();
      setLinkUrl("");
      setLinkError("");
      setPanel("link");
    };

    const submitLink = () => {
      const url = toLinkUrl(linkUrl);
      if (!url) {
        setLinkError(t("htmlEditor.linkInvalid") as string);
        return;
      }
      setPanel(null);
      executeCommand("createLink", url);
    };

    const pickerRange = useRef<Range | null>(null);

    const openColourPicker =
      (property: "color" | "background-color", fallback: string) =>
      (input: HTMLInputElement) => {
        pickerRange.current = savedRange.current?.cloneRange() ?? null;
        input.value = colourAt(
          pickerRange.current,
          editorRef.current,
          property,
          fallback
        );
      };

    // A click in the text while the panel is open leaves a caret: the colour stays on the words chosen first.
    const applyColour = (command: string) => (colour: string) => {
      const live = savedRange.current;
      executeCommand(
        command,
        colour,
        live && !live.collapsed ? live : pickerRange.current
      );
      pickerRange.current = savedRange.current?.cloneRange() ?? null;
    };

    useEffect(() => {
      if (panel === "link") linkInputRef.current?.focus();
      if (!panel) return;
      const panelId = panel === "link" ? linkPanelId : emojiPanelId;
      const onPointerDown = (event: MouseEvent) => {
        const target = event.target as Element;
        if (
          panelRef.current?.contains(target) ||
          target.closest?.(`[aria-controls="${panelId}"]`)
        )
          return;
        setPanel(null);
      };
      document.addEventListener("mousedown", onPointerDown);
      return () => document.removeEventListener("mousedown", onPointerDown);
    }, [panel, linkPanelId, emojiPanelId]);

    useLayoutEffect(() => {
      const items = toolbarItems(toolbarRef.current);
      focusOnly(
        toolbarRef.current,
        toolbarFocus.current && items.includes(toolbarFocus.current)
          ? toolbarFocus.current
          : items[0]
      );
    });

    const onToolbarKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      if (!target.hasAttribute("data-toolbar-item")) return;
      const items = toolbarItems(toolbarRef.current);
      const index = items.indexOf(target);
      const moves: Record<string, number> = {
        ArrowRight: index + 1,
        ArrowLeft: index - 1,
        Home: 0,
        End: items.length - 1,
      };
      if (index < 0 || !(event.key in moves)) return;
      event.preventDefault();
      const next = items[(moves[event.key] + items.length) % items.length];
      toolbarFocus.current = next;
      focusOnly(toolbarRef.current, next);
      next.focus();
    };

    const onToolbarFocus = (event: React.FocusEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      if (!target.hasAttribute("data-toolbar-item")) return;
      toolbarFocus.current = target;
      focusOnly(toolbarRef.current, target);
    };

    useImperativeHandle(
      ref,
      () => ({
        focus: () => editorRef.current?.focus(),
        blur: () => editorRef.current?.blur(),
        getHtml: () =>
          editorRef.current ? toHtml(editorRef.current.innerHTML) : "",
        setHtml: replaceContent,
        isEmpty: () =>
          countCharacters(editorRef.current?.textContent?.trim() ?? "") === 0,
        clear: () => replaceContent(""),
      }),
      [replaceContent, toHtml]
    );

    const label = (key: string) => t(`htmlEditor.${key}`) as string;
    const toggle = (
      command: ToggleCommand,
      icon: React.ReactNode,
      keys?: string
    ) => (
      <ToolbarButton
        icon={icon}
        label={label(TOGGLE_LABELS[command])}
        keys={keys}
        pressed={Boolean(toolbar.active[command])}
        disabled={disabled}
        onAction={() => executeCommand(command)}
      />
    );
    const separator = <div className="w-px h-6 bg-gray-300" />;

    return (
      <div
        className={`html-editor border border-gray-300 rounded-lg bg-white focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 ${className}`}
        style={style}
        translate="no"
      >
        <div
          ref={toolbarRef}
          role="toolbar"
          aria-label={label("toolbar")}
          onKeyDown={onToolbarKeyDown}
          onFocus={onToolbarFocus}
          className="relative flex flex-wrap items-center gap-1 p-2 border-b border-gray-200 bg-gray-50"
        >
          {full && (
            <>
              <ToolbarSelect
                label={label("blockFormat")}
                value={toolbar.block}
                disabled={disabled}
                onPick={applyBlock}
                options={BLOCK_TAGS.map((tag) => ({
                  value: tag,
                  label: label(`blocks.${tag}`),
                }))}
              />
              {separator}
            </>
          )}

          {full && (
            <>
              <ToolbarSelect
                label={label("fontSize")}
                value={toolbar.fontSize}
                disabled={disabled}
                onPick={applyFontSize}
                options={Object.keys(FONT_SIZES).map((size) => ({
                  value: size,
                  label: label(`sizes.${size}`),
                }))}
              />
              {separator}
            </>
          )}

          {toggle("bold", <BiBold size={16} />, shortcut("B"))}
          {toggle("italic", <BiItalic size={16} />, shortcut("I"))}
          {toggle("underline", <BiUnderline size={16} />, shortcut("U"))}
          {toggle("strikeThrough", <BiStrikethrough size={16} />)}

          {separator}

          {full && (
            <>
              <ColourPicker
                label={label("textColor")}
                swatch={<BiText size={16} className="text-red-600" />}
                disabled={disabled}
                onOpen={openColourPicker("color", palette.black)}
                onPick={applyColour("foreColor")}
              />
              <ColourPicker
                label={label("backgroundColor")}
                swatch={
                  <span className="inline-block w-4 h-3 border border-gray-400 bg-amber-400 rounded-sm" />
                }
                disabled={disabled}
                onOpen={openColourPicker("background-color", palette.white)}
                onPick={applyColour("hiliteColor")}
              />
              {separator}
            </>
          )}

          {full && (
            <>
              {toggle("subscript", <span className="text-xs">x₂</span>)}
              {toggle("superscript", <span className="text-xs">x²</span>)}
              {separator}
            </>
          )}

          {toggle("insertUnorderedList", <BiListUl size={16} />)}
          {toggle("insertOrderedList", <BiListOl size={16} />)}

          {separator}

          {full && (
            <>
              <ToolbarButton
                icon={<MdFormatIndentDecrease size={16} />}
                label={label("outdent")}
                disabled={disabled}
                onAction={() => executeCommand("outdent")}
              />
              <ToolbarButton
                icon={<MdFormatIndentIncrease size={16} />}
                label={label("indent")}
                disabled={disabled}
                onAction={indent}
              />

              {separator}

              {toggle("justifyLeft", <BiAlignLeft size={16} />)}
              {toggle("justifyCenter", <MdFormatAlignCenter size={16} />)}
              {toggle("justifyRight", <BiAlignRight size={16} />)}
              {toggle("justifyFull", <MdFormatAlignJustify size={16} />)}

              {separator}

              <ToolbarButton
                icon={<BiLink size={16} />}
                label={label("link")}
                disabled={disabled}
                expanded={panel === "link"}
                controls={linkPanelId}
                onAction={() =>
                  panel === "link" ? closePanel() : openLinkPanel()
                }
              />
              <ToolbarButton
                icon={<BiUnlink size={16} />}
                label={label("unlink")}
                disabled={disabled}
                onAction={() => executeCommand("unlink")}
              />

              {separator}

              <ToolbarButton
                icon={<BiCode size={16} />}
                label={label("code")}
                disabled={disabled}
                onAction={insertInlineCode}
              />

              {separator}
            </>
          )}

          <ToolbarButton
            icon={<span className="text-base">😊</span>}
            label={label("emoji")}
            disabled={disabled}
            expanded={panel === "emoji"}
            controls={emojiPanelId}
            onAction={() =>
              panel === "emoji" ? closePanel() : setPanel("emoji")
            }
          />

          {separator}

          <ToolbarButton
            icon={<BiUndo size={16} />}
            label={label("undo")}
            keys={shortcut("Z")}
            disabled={disabled || !canUndo}
            onAction={() => stepHistory(-1)}
          />
          <ToolbarButton
            icon={<BiRedo size={16} />}
            label={label("redo")}
            keys={shortcut("Z", true)}
            disabled={disabled || !canRedo}
            onAction={() => stepHistory(1)}
          />

          {panel === "link" && (
            <div
              ref={panelRef}
              id={linkPanelId}
              className="absolute inset-x-2 top-full z-50 mt-1 rounded-lg border border-gray-200 bg-white p-3 shadow-lg sm:inset-x-auto sm:left-2 sm:w-80 space-y-2"
            >
              <label
                htmlFor={`${linkPanelId}-input`}
                className="block text-sm font-medium text-gray-700"
              >
                {label("linkAddress")}
              </label>
              <input
                ref={linkInputRef}
                id={`${linkPanelId}-input`}
                type="url"
                inputMode="url"
                value={linkUrl}
                placeholder={label("linkPlaceholder")}
                aria-invalid={Boolean(linkError)}
                aria-describedby={
                  linkError ? `${linkPanelId}-error` : undefined
                }
                onChange={(event) => {
                  setLinkUrl(event.target.value);
                  setLinkError("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    submitLink();
                  } else if (event.key === "Escape") {
                    event.preventDefault();
                    event.stopPropagation();
                    closePanel();
                  }
                }}
                className="block w-full rounded-md border border-gray-300 bg-white p-2 text-base text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 md:text-sm"
              />
              {linkError && (
                <p
                  id={`${linkPanelId}-error`}
                  className="text-xs text-red-600"
                  role="alert"
                >
                  {linkError}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <CustomButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  title={t("common.cancel") as string}
                  onClick={closePanel}
                />
                <CustomButton
                  type="button"
                  variant="primary"
                  size="sm"
                  title={label("linkInsert")}
                  onClick={submitLink}
                />
              </div>
            </div>
          )}

          {panel === "emoji" && (
            <div
              ref={panelRef}
              id={emojiPanelId}
              className="absolute inset-x-2 top-full z-50 mt-1 rounded-lg border border-gray-200 bg-white p-3 shadow-lg sm:inset-x-auto sm:left-2 sm:w-80"
              onKeyDown={(event) => {
                if (event.key !== "Escape") return;
                event.preventDefault();
                event.stopPropagation();
                closePanel();
              }}
            >
              <div className="grid grid-cols-8 gap-1 max-h-48 overflow-y-auto">
                {EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => insertEmoji(emoji)}
                    className="h-8 w-full flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors text-lg"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div
          translate="no"
          ref={editorRef}
          role="textbox"
          aria-multiline="true"
          aria-label={ariaLabel ?? placeholderText}
          aria-disabled={disabled || undefined}
          contentEditable={!disabled}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onDrop={handleDrop}
          onDragStart={() => {
            dragFromEditor.current = true;
          }}
          onDragEnd={() => {
            dragFromEditor.current = false;
          }}
          onFocus={onFocus}
          onBlur={onBlur}
          className={`px-2 py-3 sm:px-3 focus:outline-none ${
            disabled ? "bg-gray-50 cursor-not-allowed" : ""
          }`}
          style={{
            minHeight,
            maxHeight,
            overflowY: "auto",
            overflowX: "auto",
            wordWrap: "normal",
            wordBreak: "normal",
            ...style,
          }}
          suppressContentEditableWarning={true}
          data-placeholder={placeholderText}
        />

        {(showCharacterCount || maxLength) && (
          <div className="px-3 py-2 bg-transparent text-right">
            <span
              className={`text-xs ${
                maxLength && characterCount > maxLength
                  ? "text-red-500"
                  : maxLength && characterCount > maxLength * 0.9
                  ? "text-amber-500"
                  : "text-gray-500"
              }`}
            >
              {characterCount}
              {maxLength ? `/${maxLength}` : ""}
            </span>
          </div>
        )}
      </div>
    );
  }
);

HtmlEditor.displayName = "HtmlEditor";

export default HtmlEditor;
